import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { AuthUser, LoginRequest, LoginResponse } from "@bi/types";
import { api, tokenStore } from "./api";

interface AuthState {
  user: AuthUser | null;
  ready: boolean;
  signIn: (creds: LoginRequest) => Promise<void>;
  signOut: () => void;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [ready, setReady] = useState(false);

  // tenta restaurar sessão a partir do token salvo
  useEffect(() => {
    const t = tokenStore.get();
    if (!t) { setReady(true); return; }
    api.get<AuthUser>("/me")
      .then((u) => setUser(u))
      .catch(() => tokenStore.clear())
      .finally(() => setReady(true));
  }, []);

  // Empresa de demonstração usa a identidade azul (variáveis em globals.css).
  useEffect(() => {
    if (user?.isDemo) document.documentElement.dataset.brand = "demo";
    else delete document.documentElement.dataset.brand;
  }, [user?.isDemo]);

  const value = useMemo<AuthState>(() => ({
    user,
    ready,
    async signIn(creds) {
      const res = await api.post<LoginResponse>("/auth/login", creds);
      tokenStore.set(res.token);
      setUser(res.user);
    },
    signOut() {
      tokenStore.clear();
      setUser(null);
    },
  }), [user, ready]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth fora do AuthProvider");
  return ctx;
}
